import { List, ListItem, Tooltip, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { css } from '@emotion/react';
import { colors } from '../../../theme/colors';

/**
 * `border/default/tooltip` from Figma — kept local because the token system
 * has no `colors.border.tooltip` entry yet. Promote if reused more broadly.
 */
const borderTooltip = '#D5D7EB';

/**
 * Bordered "+N" overflow chip. Uses `min-width` + horizontal padding so the
 * chip scales gracefully for large counts (e.g. "+12") without clipping —
 * the original sidebar version was locked to 24×24 which broke for 2+ digits.
 */
const chipMoreCss = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 4px;
  flex-shrink: 0;
  border: 1px solid ${borderTooltip};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.66;
  color: ${colors.secondary[50]};
  box-sizing: border-box;
  cursor: default;
`;

/**
 * Styled inner surface for the tooltip popover so the overflow list renders
 * with generous padding and scrolls when the list exceeds the viewport —
 * matches `TooltipContent` from the legacy `ListWithMoreChip` treatment so
 * consumers keep a consistent affordance across the app.
 */
const TooltipContent = styled('div')`
  padding: 1rem 1rem 0;
  max-height: 20rem;
  overflow-y: auto;
`;

export interface MoreItemsTooltipProps {
  /**
   * Plural label for the hidden items (e.g. "Investors", "Aliases"). Used to
   * build the tooltip heading: `Other <label> (N)`.
   */
  readonly label: string;
  /** The overflow items to list inside the tooltip. Must be non-empty. */
  readonly items: readonly string[];
}

/**
 * `+N` overflow chip with an MUI Tooltip listing the hidden items.
 *
 * Pure presentational component — callers are responsible for deciding how
 * many items are visible and passing the remainder here.
 */
export function MoreItemsTooltip({ label, items }: MoreItemsTooltipProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <Tooltip
      placement='top-start'
      componentsProps={{ tooltip: { sx: { color: 'black' } } }}
      title={
        <TooltipContent>
          <Typography component='h6'>{`Other ${label} (${items.length})`}</Typography>
          <List>
            {items.map((item) => (
              <ListItem key={item} sx={{ paddingLeft: 0, height: '2.2rem' }}>
                <Typography variant='body2' component='span' noWrap>
                  {item}
                </Typography>
              </ListItem>
            ))}
          </List>
        </TooltipContent>
      }
    >
      <span css={chipMoreCss} aria-label={`more ${label.toLowerCase()}`}>
        +{items.length}
      </span>
    </Tooltip>
  );
}
